// @flow

import $ from 'jquery';
import { retrieve } from 'putki';
import currentSite from 'site/currentSite';
import getCookies from 'utils/getCookies';
import { trackEvent, trackClicksOn } from 'utils/analytics';
import {
  object,
  record,
  nullable,
  string,
  withDefault
} from 'common/json/extract';

import { retrieveRemarketingProperties } from './analytics/remarketing';

/**
 * Rebinds tracking of anchor elements with data-venuu-t attributes.
 */
function rebindTrackedLinks() {
  trackClicksOn($('a[data-venuu-t]:not([data-venuu-t-react])'));
}

/**
 * Sends an "Arrived from Campaign" tracking event, if visitor arrived from
 * a Venuu campaign site.
 */
function trackReferringCampaigns() {
  type CampaignDataObject = {
    'Campaign Source': string,
    'Campaign Name': string,
    'Campaign Medium'?: string,
    ...
  };

  const campaignDataFromUrl: string => null | CampaignDataObject = urlParam => {
    let url;
    try {
      // URLs which purl can't parse might throw. Don't throw in that case.
      // See https://github.com/venuu/venuu/issues/749
      url = $.url(urlParam);
    } catch (e) {
      return null;
    }

    const soundboard = url.attr('host').match(/(cheek|putous)\.venuu.fi/i);
    if (soundboard) {
      // Real result should be in the second cell of Array returned by match,
      // but browsers might differ on whether they return the full match as the
      // first cell or not. Work around that pre-emptively.
      const campaignName =
        soundboard.length === 2 ? soundboard[1] : soundboard[0];
      return {
        'Campaign Source': 'Soundboard',
        'Campaign Name': campaignName.toLowerCase()
      };
    } else {
      return null;
    }
  };

  const campaignData = campaignDataFromUrl(window.document.referrer);
  if (campaignData) {
    campaignData['Campaign Medium'] = 'Referral';
    window.analytics.identify(campaignData);
    trackEvent('Arrived from Campaign', campaignData);
  }
}

const extractUserTrackingData = withDefault(
  object(
    record({
      email: nullable(string),
      customer_type: nullable(string)
    })
  ),
  { email: null, customer_type: null }
);

function distinctIdFromUrl() {
  try {
    // URLs which purl can't parse might throw. Don't throw in that case.
    // See https://github.com/venuu/venuu/issues/749
    return $.url().param('distinct_id');
  } catch (e) {
    return null;
  }
}

/**
 * The following functions are a part of the improved user tracking scheme.
 * ------------------------------------------------
 * Documentation: https://github.com/venuu/venuu/pull/7368
 */
function identifyUser() {
  const userIdFromUrl = distinctIdFromUrl();
  const userIdFromCookie = getCookies()['cheekify_uid'];
  const { email, customer_type: customerType } = extractUserTrackingData(
    retrieve('user_tracking_data')
  );

  const commonTraits = {
    'Application Version': retrieve('application_version'),
    'Site Key': currentSite().key
  };

  if (userIdFromCookie && email) {
    const traits = {
      ...commonTraits,
      Source: 'Venuu web app',
      'Customer Type': customerType,
      email: email
    };
    window.analytics.identify(userIdFromCookie, traits);
  } else if (userIdFromUrl) {
    // These are returning users
    // so they should customer identification traits set.
    window.analytics.identify(userIdFromUrl, commonTraits);
  }
}

function syncAnonTrackingCookie() {
  // Sync mixpanel distinct id to cheekify_a to maintain user identety for
  // for server-side tracking.
  window.analytics.ready(() => {
    if (window.mixpanel) {
      $.cookie('cheekify_a', window.mixpanel.get_distinct_id(), {
        path: '/',
        expires: 365
      });
    }
  });
}

export default function initializeAnalytics() {
  identifyUser();

  syncAnonTrackingCookie();

  const pageOpts = retrieveRemarketingProperties();

  window.analytics.page(pageOpts);

  // Track special links
  rebindTrackedLinks();

  trackReferringCampaigns();
}
